import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Timeline } from 'vis-timeline/standalone';
import { DataSet } from 'vis-data/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';

interface EventSummary {
  start_time: string;
  duration: string;
  item_type: string;
  location: string;
  device_id?: string;
  device_name?: string;
}

interface EventTimelineProps {
  events: EventSummary[];
  onRequestOlder?: () => void;
  timeZone: string;
  selectedKey?: string;
}

// Helper to format duration as HH:MM:SS
function formatDuration(seconds: number): string {
  seconds = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

function EventTimeline({ events, onRequestOlder, timeZone, selectedKey }: EventTimelineProps) {
  const timelineRef = useRef<Timeline | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastOlderTriggerRef = useRef<number>(0);
  const initialCheckDoneRef = useRef<boolean>(false);
  const prevWindowRef = useRef<{ start: Date; end: Date } | null>(null);
  const suppressRangeChangedRef = useRef<boolean>(false);

  // Assign a consistent color to each event_type globally (hash-based)
  const palette = [
    '#1976d2',
    '#388e3c',
    '#fbc02d',
    '#d32f2f',
    '#7b1fa2',
    '#0288d1',
    '#c2185b',
    '#8bc34a',
    '#ff9800',
    '#009688',
    '#e91e63',
    '#3f51b5',
  ];

  // Simple hash function for string to int
  function hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0; // Convert to 32bit int
    }
    return Math.abs(hash);
  }

  const colorOverrides: Record<string, string> = {
    human_detected: '#22c55e',
    bathroom_trip: '#000000',
  };

  const deviceColorOverrides: Record<string, string> = {
    dv50cc67: '#d946ef',
  };

  const activeDeviceOverrides: Record<string, string> = {};
  events?.forEach(event => {
    if (!event.device_id) return;
    const override = deviceColorOverrides[event.device_id];
    if (override) {
      activeDeviceOverrides[event.device_id] = override;
    }
  });

  const eventTypeColors: Record<string, string> = {};
  events?.forEach(e => {
    const type = e.item_type || 'Unknown';
    if (eventTypeColors[type]) return;

    const normalized = type.toLowerCase();
    const override = colorOverrides[normalized];
    if (override) {
      eventTypeColors[type] = override;
      return;
    }

    const idx = hashString(type) % palette.length;
    eventTypeColors[type] = palette[idx];
  });

  const formatWithTZ = (d: Date) => {
    try {
      return d.toLocaleString('en-US', {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return d.toLocaleString();
    }
  };

  const earliestEventStart = useMemo(() => {
    if (!Array.isArray(events) || events.length === 0) return null;
    let min = Number.POSITIVE_INFINITY;
    for (const e of events) {
      const ts = Date.parse(e.start_time);
      if (!Number.isNaN(ts) && ts < min) min = ts;
    }
    return Number.isFinite(min) ? min : null;
  }, [events]);

  useEffect(() => {
    if (!containerRef.current || !Array.isArray(events) || events.length === 0)
      return;

    // Prepare visjs items and groups
    const items: any[] = [];
    const groupsMap = new Map<string, number>();
    let groupId = 1;

    events.forEach((event, idx) => {
      const start = new Date(event.start_time);

      // Parse duration in HH:MM:SS format to seconds
      function parseDuration(hms: string): number {
        if (typeof hms !== 'string') return 0;
        const parts = hms.split(':').map(Number);
        if (parts.length === 3) {
          return parts[0] * 3600 + parts[1] * 60 + parts[2];
        } else if (parts.length === 2) {
          return parts[0] * 60 + parts[1];
        } else if (parts.length === 1) {
          return parts[0];
        }
        return 0;
      }
      const duration = parseDuration(event.duration);
      const end = new Date(start.getTime() + duration * 1000);
      const displayType = event.device_id === 'dv50cc67' ? 'bedroom_presence' : (event.item_type || 'Unknown Type');
      const groupKey = `${event.device_name || 'Unknown Device'} / ${displayType}`;

      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, groupId++);
      }
      const group = groupsMap.get(groupKey);

      // Use event_type color and remove the black border for a cleaner look
      let color = eventTypeColors[displayType || 'Unknown'];
      if (event.device_id && activeDeviceOverrides[event.device_id]) {
        color = activeDeviceOverrides[event.device_id];
      }
      const key = `${event.device_id || 'ALL'}|${event.start_time}|${displayType}`;
      items.push({
        id: key,
        content: '',
        start,
        end,
        group,
        style: `
          background: ${color};
          opacity: 0.95;
          box-shadow: 0 0 0 2px #fff, 0 2px 8px 0 rgba(0,0,0,0.15);
        `,
        title: `
          <div>
            <b>Device:</b> ${event.device_name || 'Unknown'}<br/>
            <b>Type:</b> ${displayType || 'Unknown'}<br/>
            <b>Location:</b> ${event.location || 'Unknown'}<br/>
            <b>Start:</b> ${formatWithTZ(start)}<br/>
            <b>Duration:</b> ${formatDuration(duration)}<br/>
            <b>End:</b> ${formatWithTZ(end)}
          </div>
        `,
      });
    });

    const groups = Array.from(groupsMap.entries()).map(([label, id]) => ({
      id,
      content: '', // Hide group labels for a cleaner look
    }));

    // Preserve current window before rebuild
    try {
      if (timelineRef.current) {
        const w = (timelineRef.current as any)?.getWindow?.();
        if (w && w.start && w.end) {
          prevWindowRef.current = { start: w.start, end: w.end };
        }
      }
    } catch {
      // ignore
    }

    // Destroy previous timeline if exists
    if (timelineRef.current) {
      timelineRef.current.destroy();
      timelineRef.current = null;
    }

    // Create new timeline
    timelineRef.current = new Timeline(
      containerRef.current,
      new DataSet(items),
      new DataSet(groups),
      {
        stack: false, // allow overlaps to be visible
        groupOrder: 'content',
        orientation: 'top',
        margin: { item: 8, axis: 24 },
        tooltip: { followMouse: true },
        minHeight: 200,
        maxHeight: 400,
        showCurrentTime: true,
        zoomMin: 1000 * 60 * 1, // 1 minute
        zoomMax: 1000 * 60 * 60 * 24 * 30, // 1 month
      }
    );

    // Restore previous timeline window to avoid jarring resets
    if (prevWindowRef.current && timelineRef.current) {
      try {
        suppressRangeChangedRef.current = true;
        (timelineRef.current as any).setWindow(
          prevWindowRef.current.start,
          prevWindowRef.current.end,
          { animation: false }
        );
      } catch {
        // ignore
      } finally {
        // Allow a microtask before re-enabling to avoid firing immediately
        setTimeout(() => {
          suppressRangeChangedRef.current = false;
        }, 0);
      }
    }

    const thresholdMs = 1000 * 60 * 60 * 6; // 6 hours
    const onRangeChanged = (props: any) => {
      if (!onRequestOlder || earliestEventStart == null) return;
      if (suppressRangeChangedRef.current) return;
      try {
        if (!props || !(props.start instanceof Date)) return;
        const startMs = props.start.getTime();
        if (!startMs) return;
        // If the visible window start is within threshold of earliest event, ask for older
        if (startMs - earliestEventStart <= thresholdMs) {
          const now = Date.now();
          if (now - lastOlderTriggerRef.current > 1000) {
            lastOlderTriggerRef.current = now;
            onRequestOlder();
          }
        }
      } catch {
        // ignore
      }
    };

    // Attach handler
    timelineRef.current.on('rangechanged', onRangeChanged);

    // Kick a single initial check with current window for the lifetime of the component
    if (!initialCheckDoneRef.current) {
      try {
        const win = (timelineRef.current as any)?.getWindow?.();
        if (win && win.start) {
          initialCheckDoneRef.current = true;
          onRangeChanged({ start: win.start });
        }
      } catch {
        // ignore
      }
    }

    // Cleanup on unmount
    return () => {
      if (timelineRef.current) {
        try {
          timelineRef.current.off('rangechanged', onRangeChanged);
        } catch {
          // ignore
        }
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
    };
  }, [events, earliestEventStart, onRequestOlder, timeZone]); // eslint-disable-line

  // Focus selection when requested
  useEffect(() => {
    if (!selectedKey || !timelineRef.current) return;
    try {
      (timelineRef.current as any).setSelection(selectedKey, { focus: true });
    } catch {
      // ignore
    }
  }, [selectedKey, events]);

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        border: '1px solid #eee',
        borderRadius: 2,
        background: '#fafbfc',
      }}
    >
      <Typography variant='subtitle1' sx={{ mb: 1 }}>
        Timeline of Events
      </Typography>
      <div
        ref={containerRef}
        style={{ width: '100%', minHeight: 120, height: 240 }}
      />
      {/* Legend */}
      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {Object.entries(activeDeviceOverrides).map(([deviceId, color]) => (
          <Box key={`device-${deviceId}`} sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                background: color,
                borderRadius: 1,
                mr: 1,
              }}
            />
            <Typography variant='caption'>{'Device ' + deviceId + ' (bedroom)'}</Typography>
          </Box>
        ))}
        {Object.entries(eventTypeColors).map(([type, color]) => (
          <Box key={type} sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                background: color,
                borderRadius: 1,
                mr: 1,
              }}
            />
            <Typography variant='caption'>{type}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default EventTimeline;
